/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Vision UI Dashboard React example components
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import PageLayout from "examples/LayoutContainers/PageLayout";

// Authentication layout components
import Footer from "layouts/authentication/components/Footer";

// Custom components
import AnimatedText from "components/AnimatedText";
import Prism from "components/Prism";

// React icons
import { BsRobot, BsChatDots } from "react-icons/bs";
import { IoRocketSharp } from "react-icons/io5";

// Vision UI Dashboard React theme functions
import colors from "assets/theme/base/colors";

// Vision UI Dashboard React theme functions
import tripleLinearGradient from "assets/theme/functions/tripleLinearGradient";

function CoverLayout({
  color = "info",
  header = "",
  title = "",
  description = "",
  motto = "",
  premotto = "",
  image,
  top = 20,
  cardContent,
  children,
}) {
  const { gradients } = colors;
  return (
    <PageLayout
      background={tripleLinearGradient(
        gradients.cover.main,
        gradients.cover.state,
        gradients.cover.stateSecondary,
        gradients.cover.angle
      )}
    >
      <DefaultNavbar />
      <VuiBox
        height="100%"
        width="50vw"
        display={{ xs: "none", md: "block" }}
        position="absolute"
        top={0}
        left={0}
        sx={({ breakpoints }) => ({
          overflow: "hidden",
          [breakpoints.down("xl")]: {
            mr: "100px",
          },
          [breakpoints.down("lg")]: {
            display: "none",
          },
        })}
        zIndex={0}
      >
        <VuiBox
          height="100%"
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(135deg, rgba(0, 117, 255, 0.3) 0%, rgba(138, 43, 226, 0.3) 100%)",
              zIndex: 1,
            },
          }}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Prism
            height={3.5}
            baseWidth={5.5}
            animationType="3drotate"
            glow={0.7}
            noise={0.2}
            transparent={true}
            scale={3.6}
            hueShift={0}
            colorFrequency={1.2}
            hoverStrength={2}
            inertia={0.05}
            bloom={0.8}
            timeScale={0.5}
          />
          <VuiBox
            sx={{
              position: "relative",
              zIndex: 2,
              px: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: cardContent ? "center" : "flex-start",
            }}
          >
            <VuiTypography
              textAlign={cardContent ? "center" : "start"}
              variant="subtitle1"
              fontWeight="medium"
              color="white"
              mb="20px"
              sx={{ 
                mb: 2, 
                letterSpacing: "8px",
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                animation: "fadeInDown 0.8s ease-out",
                "@keyframes fadeInDown": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(-20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              {premotto}
            </VuiTypography>
            <AnimatedText
              textAlign={cardContent ? "center" : "start"}
              variant="h2"
              fontWeight="bold"
              color="logo"
              textGradient
              sx={{ 
                letterSpacing: "4px",
                width: "100%",
              }}
            />
            <VuiBox
              mt={3}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: cardContent ? "center" : "flex-start",
                animation: "fadeInUp 1s ease-out 0.3s both",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <VuiBox
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BsRobot size="16px" color="white" />
                <VuiTypography variant="caption" color="white" fontWeight="medium">
                  AI-Powered
                </VuiTypography>
              </VuiBox>
              <VuiBox
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BsChatDots size="16px" color="white" />
                <VuiTypography variant="caption" color="white" fontWeight="medium">
                  24/7 Support
                </VuiTypography>
              </VuiBox>
              <VuiBox
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <IoRocketSharp size="16px" color="white" />
                <VuiTypography variant="caption" color="white" fontWeight="medium">
                  Auto Sales
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>
      <VuiBox
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
          alignItems: "center",
          maxWidth: "1044px",
          minHeight: "75vh",
          margin: "0 auto",
        }}
      >
        <VuiBox
          mt={top}
          ml="auto !important"
          sx={({ breakpoints }) => ({
            [breakpoints.down("xl")]: {
              mr: cardContent ? "50px" : "100px",
            },
            [breakpoints.down("lg")]: {
              mr: "auto",
              ml: "auto !important",
            },
            [breakpoints.down("md")]: {
              maxWidth: "90%",
              pr: "7px",
              pl: "10px !important",
            },
          })}
        >
          <VuiBox pt={3} px={3} mx="auto !important" maxWidth={cardContent ? "400px" : "350px"}>
            {!header ? (
              <>
                <VuiBox mb="35px">
                  <VuiTypography
                    textAlign={cardContent ? "center" : "start"}
                    variant="h3"
                    fontWeight="bold"
                    color={color}
                    mb="10px"
                  >
                    {title}
                  </VuiTypography>
                  <VuiTypography
                    textAlign={cardContent ? "center !important" : "start !important"}
                    mx="auto"
                    sx={({ typography: { size }, functions: { pxToRem } }) => ({
                      fontWeight: "regular",
                      fontSize: size.sm,
                    })}
                    color="white"
                  >
                    {description}
                  </VuiTypography>
                </VuiBox>
              </>
            ) : (
              header
            )}
          </VuiBox>
          <VuiBox
            px={3}
            mb="50px"
            mx="auto"
            ml="auto !important"
            sx={({ breakpoints }) => ({
              mt: cardContent ? "60px" : { top },
              maxWidth: cardContent ? "450px" : "350px",
              [breakpoints.down("xl")]: {
                mr: cardContent ? "0px" : "100px",
              },
              [breakpoints.only("lg")]: {
                mr: "auto",
                ml: "auto !important",
              },
              [breakpoints.down("lg")]: {
                mr: "auto",
                ml: "auto !important",
              },
              [breakpoints.down("md")]: {
                mr: cardContent ? "auto !important" : "unset",
                pr: "7px",
                pl: cardContent ? "0px !important" : "10px !important",
              },
            })}
          >
            {children}
          </VuiBox>
          <Footer />
        </VuiBox>
      </VuiBox>
    </PageLayout>
  );
}

// Typechecking props for the CoverLayout
CoverLayout.propTypes = {
  header: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string.isRequired,
  top: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export default CoverLayout;
